import PageTitle from '@/components/PageTitle';
import { Heading, Text } from '@/components/Typography';
import AuthLayout from '@/layouts/AuthLayout';
import clsx from 'clsx';
import { Link, useForm } from '@inertiajs/react';
import { CheckCircle, Mail } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ForgotPassword = () => {
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);

    const { data, setData, post, errors, processing } = useForm<{
        email: string;
    }>({
        email: '',
    });

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        post('/auth/forgot-password/', {
            onSuccess: () => setSubmitted(true),
        });
    };

    if (submitted) {
        return (
            <AuthLayout>
                <PageTitle title="Recuperar Contrasena" />
                <div className="w-full">
                    <div className="mb-6 text-center">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                            <CheckCircle className="h-8 w-8 text-green-600" />
                        </div>
                        <Heading as="h1" className="mb-3 text-2xl font-semibold text-green-700">
                            {t('auth.forgotPassword.successTitle')}
                        </Heading>
                        <Text size="sm" muted>
                            {t('auth.forgotPassword.successMessage')}
                        </Text>
                    </div>

                    <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-start gap-3">
                            <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-700" />
                            <p className="text-sm text-gray-700">
                                {t('auth.forgotPassword.checkInbox')}
                            </p>
                        </div>
                    </div>

                    <div className="text-center">
                        <Link
                            href="/auth/login/"
                            className="text-sm font-medium text-gray-900 hover:underline"
                        >
                            {t('auth.forgotPassword.backToLogin')}
                        </Link>
                    </div>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <PageTitle title="Recuperar Contrasena" />
            <div className="w-full">
                <div className="mb-6 text-center">
                    <Heading as="h1" className="mb-3 text-2xl font-semibold">
                        {t('auth.forgotPassword.title')}
                    </Heading>
                    <Text size="sm" muted>
                        {t('auth.forgotPassword.description')}
                    </Text>
                </div>

                <form onSubmit={onSubmit}>
                    <div className="flex flex-col gap-y-5">
                        <div>
                            <label
                                htmlFor="email"
                                className="mb-1.5 block text-sm font-medium text-gray-700"
                            >
                                {t('auth.forgotPassword.email')}
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={data.email}
                                onChange={(e) => setData('email', e.target.value)}
                                placeholder={t('home.emailPlaceholder')}
                                className={clsx(
                                    'w-full rounded-md border px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2',
                                    errors.email
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                                        : 'border-gray-300 focus:border-gray-400 focus:ring-gray-200'
                                )}
                            />
                            {errors.email && (
                                <p className="mt-1 text-xs text-red-500">
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={processing}
                            className="inline-flex w-full items-center justify-center gap-x-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {processing && (
                                <svg
                                    aria-hidden="true"
                                    role="status"
                                    className="inline h-4 w-4 animate-spin text-white opacity-75"
                                    viewBox="0 0 100 101"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                        fill="currentColor"
                                    ></path>
                                    <path
                                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                        fill="white"
                                    ></path>
                                </svg>
                            )}
                            {processing
                                ? t('auth.forgotPassword.loading')
                                : t('auth.forgotPassword.submit')}
                        </button>

                        <div className="pt-1 text-center">
                            <Link
                                href="/auth/login/"
                                className="text-sm font-medium text-gray-900 hover:underline"
                            >
                                {t('auth.forgotPassword.backToLogin')}
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
        </AuthLayout>
    );
};

export default ForgotPassword;
